import React, { useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import Input from 'react-phone-number-input/input';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useNavigate } from 'react-router-dom';
import { ArrowBack as ArrowBackIcon, Close as CloseIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  FormLabel,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import {
  COLOR_DENIM,
  COLOR_DISABLED_BG,
  COLOR_DISABLED_TEXT,
  COLOR_GALLERY,
  COLOR_HUMMINGBIRD,
  COLOR_RED,
  COLOR_REGENT_ST_BLUE,
  COLOR_WHITE,
  FONT_WEIGHT_SEMI_BOLD,
} from 'popTheme';
import { PopInputField } from './PopInputField';
import PopPhoneInputField from './PopPhoneInputField';
import { ProvinceSelect } from './ProvinceSelect';
import { ScrollableContainer } from './ScrollableContainer';
import { YearSelect } from './YearSelect';
import { registerSaga } from 'redux/sagas/registerSaga';

export const PersonalDetails = ({ prevStep, handleChange, values }) => {
  const theme = useTheme();
  const intl = useIntl();
  const messages = defineMessages({
    registerLabelTitle2: {
      id: 'register.label.title2',
      defaultMessage: 'A few more details...',
      description: 'Title text for register step 2 section',
    },
    registerLabelStep2: {
      id: 'register.label.step2of2',
      defaultMessage: 'Step 2 of 2',
      description: 'Step indicator text for register step 2 section',
    },
    registerLabelNickname: {
      id: 'register.label.nickname',
      defaultMessage: 'Nickname',
      description: 'Label for the nickname input',
    },
    registerLabelBirthYear: {
      id: 'register.label.birth.year',
      defaultMessage: 'Year of Birth',
      description: 'Label for the birth year dropdown',
    },
    registerLabelProvince: {
      id: 'register.label.province',
      defaultMessage: 'Province or Territory',
      description: 'Label for the province dropdown',
    },
    registerLabelPhoneNumber: {
      id: 'register.label.phone.number',
      defaultMessage: 'Cell Phone Number',
      description: 'Label for the phone number input',
    },
    registerLabelLanguage: {
      id: 'register.label.language',
      defaultMessage: 'Language',
      description: 'Label for the language toggle',
    },
    registerLabelButtonCreateAccount: {
      id: 'register.label.button.create.account',
      defaultMessage: 'Create Account',
      description: 'Label for the create account button',
    },
    registerValidationErrorMessage: {
      id: 'register.validation.error.message',
      defaultMessage: 'Unable to register with the provided email and password.',
      description: 'Error text when registration fails',
    },
    registerButtonEnglish: {
      id: 'register.button.english',
      defaultMessage: 'English',
      description: 'Register page English button label',
    },
    registerButtonFrench: {
      id: 'register.button.french',
      defaultMessage: 'French',
      description: 'Register page French button label',
    },
    registerPhoneNumberError: {
      id: 'phone.number.error.message',
      defaultMessage: 'Incorrect phone number format',
      description: 'Error text when phone number is incorrect',
    },
  });

  const handlePrevious = (e) => {
    e.preventDefault();
    prevStep();
  };

  const ref = useRef();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isSignedIn, registerError } = useSelector((state) => state.authorization);
  const sending = useSelector((state) => state.serverCommunication.sending);
  const isValidPhoneNumber =
    values.phoneNumber.length === 0 || isPossiblePhoneNumber(values.phoneNumber);
  const isDisabled =
    values.email.length === 0 ||
    values.password.length === 0 ||
    values.confirmPassword.length === 0 ||
    values.nickname.length === 0 ||
    !isValidPhoneNumber ||
    sending;

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(
      registerSaga({
        email: values.email,
        password: values.password,
        nickname: values.nickname,
        birthYear: values.birthYear,
        province: values.province,
        phoneNumber: values.phoneNumber,
        language: values.language,
      }),
    );
  };

  useEffect(() => {
    if (isSignedIn) {
      navigate('/');
    }
  });

  const toggleButtonCss = {
    height: '2.5rem',
    mt: '0.5rem',
    mb: '1rem',
    backgroundColor: COLOR_GALLERY,
    color: COLOR_DENIM,
    ':hover': {
      bgcolor: COLOR_GALLERY,
    },
    '&.Mui-selected, &.Mui-selected:hover': {
      color: COLOR_GALLERY,
      backgroundColor: COLOR_DENIM,
    },
    [theme.breakpoints.only('xs')]: {
      height: '2rem',
    },
  };

  const languageButtons = [
    {
      value: 'ENGLISH',
      text: intl.formatMessage(messages.registerButtonEnglish),
      testid: 'register-english-button',
      name: 'language',
    },
    {
      value: 'FRENCH',
      text: intl.formatMessage(messages.registerButtonFrench),
      testid: 'register-french-button',
      name: 'language',
    },
  ];

  return (
    <ScrollableContainer>
      <NavLink data-testid="register-step2-back-link" to="#" onClick={handlePrevious}>
        <ArrowBackIcon
          sx={{
            color: COLOR_REGENT_ST_BLUE,
            position: 'relative',
            fontSize: '1.25rem',
            top: '1.25rem',
            left: { xs: '1.25rem', sm: '0rem' },
          }}
        />
      </NavLink>
      <NavLink data-testid="register-step2-close-link" to="/signin">
        <CloseIcon
          sx={{
            color: COLOR_REGENT_ST_BLUE,
            position: { xs: 'absolute', sm: 'relative' },
            fontSize: '1.25rem',
            top: '1.25rem',
            right: { xs: '1.25rem', sm: '0rem' },
            left: { sm: 'calc(100% - 3rem)' },
          }}
        />
      </NavLink>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        sx={{
          [theme.breakpoints.only('xs')]: {
            gap: '0.3125rem',
            px: '3rem',
            py: '2.5rem',
          },
          [theme.breakpoints.up('sm')]: {
            gap: '0.9375rem',
            px: '3.75rem',
            py: '7.0625rem',
          },
        }}
      >
        <Typography
          variant="h2"
          sx={{
            color: COLOR_WHITE,
            textAlign: 'center',
            [theme.breakpoints.up('xs')]: {
              fontSize: '1.5625rem',
            },
            [theme.breakpoints.up('sm')]: {
              fontSize: '2rem',
            },
            [theme.breakpoints.up('lg')]: {
              fontSize: '2.4375rem',
            },
          }}
        >
          {intl.formatMessage(messages.registerLabelTitle2)}
        </Typography>
        <Typography
          sx={{
            color: COLOR_WHITE,
            [theme.breakpoints.up('xs')]: {
              fontSize: '0.8125rem',
            },
            [theme.breakpoints.up('sm')]: {
              fontSize: '1rem',
            },
            [theme.breakpoints.up('lg')]: {
              fontSize: '1.25rem',
            },
          }}
        >
          {intl.formatMessage(messages.registerLabelStep2)}
        </Typography>
        <Box
          component="form"
          data-testid="register-step2-form"
          noValidate
          alignItems="center"
          sx={{
            [theme.breakpoints.up('xs')]: {
              minWidth: '16rem',
            },
            [theme.breakpoints.up('sm')]: {
              minWidth: '26.25rem',
            },
          }}
        >
          <Box py={{ xs: '0.9375rem', sm: '0.625rem' }}>
            <FormLabel
              required
              htmlFor="register-step2-nickname-input"
              sx={{
                color: COLOR_WHITE,
                fontWeight: FONT_WEIGHT_SEMI_BOLD,
                '.MuiFormLabel-asterisk': {
                  color: COLOR_RED,
                },
                [theme.breakpoints.up('xs')]: {
                  px: '0.625rem',
                  py: '0.5rem',
                  fontSize: '0.625rem',
                },
                [theme.breakpoints.up('sm')]: {
                  px: '0.9375rem',
                  py: '0.625rem',
                  fontSize: '0.8125rem',
                },
              }}
            >
              {intl.formatMessage(messages.registerLabelNickname)}
            </FormLabel>
            <PopInputField
              onChange={handleChange('nickname')}
              defaultValue={values.nickname}
              required
              fullWidth
              id="nickname"
              name="nickname"
              autoFocus
              size="small"
              data-testid="register-step2-nickname-input"
              inputProps={{ maxLength: 128 }}
            />
          </Box>
          <Grid container columnSpacing={4}>
            <Grid item xs={12}>
              <FormLabel
                htmlFor="register-step2-birth-year-input"
                sx={{
                  color: COLOR_WHITE,
                  fontWeight: FONT_WEIGHT_SEMI_BOLD,
                  '.MuiFormLabel-asterisk': {
                    color: COLOR_RED,
                  },
                  [theme.breakpoints.up('xs')]: {
                    px: '0.625rem',
                    py: '0.5rem',
                    fontSize: '0.625rem',
                  },
                  [theme.breakpoints.up('sm')]: {
                    px: '0.9375rem',
                    py: '0.625rem',
                    fontSize: '0.8125rem',
                  },
                }}
              >
                {intl.formatMessage(messages.registerLabelBirthYear)}
              </FormLabel>
              <YearSelect
                onChange={handleChange('birthYear')}
                value={values.birthYear}
                id="birthYear"
                name="birthYear"
                data-testid="register-step2-birth-year-input"
                sxprops={{ mt: '0.5rem', mb: '1.5rem' }}
              />
            </Grid>
          </Grid>
          <FormLabel
            htmlFor="register-step2-province-input"
            sx={{
              color: COLOR_WHITE,
              fontWeight: FONT_WEIGHT_SEMI_BOLD,
              '.MuiFormLabel-asterisk': {
                color: COLOR_RED,
              },
              [theme.breakpoints.up('xs')]: {
                px: '0.625rem',
                py: '0.5rem',
                fontSize: '0.625rem',
              },
              [theme.breakpoints.up('sm')]: {
                px: '0.9375rem',
                py: '0.625rem',
                fontSize: '0.8125rem',
              },
            }}
          >
            {intl.formatMessage(messages.registerLabelProvince)}
          </FormLabel>
          <ProvinceSelect
            onChange={handleChange('province')}
            value={values.province}
            name="province"
            id="province"
            data-testid="register-step2-province-input"
            sxprops={{ mt: '0.5rem', mb: '1.5rem' }}
          />
          <FormLabel
            htmlFor="register-step2-phone-number-input"
            sx={{
              color: COLOR_WHITE,
              fontWeight: FONT_WEIGHT_SEMI_BOLD,
              '.MuiFormLabel-asterisk': {
                color: COLOR_RED,
              },
              [theme.breakpoints.up('xs')]: {
                px: '0.625rem',
                py: '0.5rem',
                fontSize: '0.625rem',
              },
              [theme.breakpoints.up('sm')]: {
                px: '0.9375rem',
                py: '0.625rem',
                fontSize: '0.8125rem',
              },
            }}
          >
            {intl.formatMessage(messages.registerLabelPhoneNumber)}
          </FormLabel>
          <Input
            country="US"
            inputComponent={PopPhoneInputField}
            onChange={handleChange('phoneNumber')}
            value={values.phoneNumber}
            id="phoneNumber"
            name="phoneNumber"
            data-testid="register-step2-phone-number-input"
            ref={ref}
          />
          {!isValidPhoneNumber && (
            <Typography variant="subtitle1" align="center" color={COLOR_WHITE} py="0.5rem">
              {intl.formatMessage(messages.registerPhoneNumberError)}
            </Typography>
          )}
          <FormLabel
            htmlFor="register-step2-language-toggle"
            sx={{
              color: COLOR_WHITE,
              fontWeight: FONT_WEIGHT_SEMI_BOLD,
              [theme.breakpoints.up('xs')]: {
                px: '0.625rem',
                py: '0.5rem',
                fontSize: '0.625rem',
              },
              [theme.breakpoints.up('sm')]: {
                px: '0.9375rem',
                py: '0.625rem',
                fontSize: '0.8125rem',
              },
            }}
          >
            {intl.formatMessage(messages.registerLabelLanguage)}
          </FormLabel>
          <ToggleButtonGroup
            fullWidth={true}
            exclusive
            onChange={handleChange('language')}
            value={values.language || 'ENGLISH'}
            data-testid={'register-step2-language-group'}
          >
            {languageButtons.map((button) => (
              <ToggleButton
                key={button.value}
                value={button.value}
                name={button.name}
                sx={toggleButtonCss}
                data-testid={button.testid}
              >
                {button.text}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Box display="flex" flexDirection="column" alignItems="center">
            <Button
              type="submit"
              onClick={handleSubmit}
              disabled={isDisabled}
              variant="contained"
              data-testid="register-step2-submit-button"
              sx={{
                mt: '2rem',
                bgcolor: COLOR_HUMMINGBIRD,
                color: COLOR_DENIM,
                minWidth: '13.125rem',
                fontWeight: 600,
                px: '0.625rem',
                ':hover': {
                  opacity: 0.5,
                },
                '&.Mui-disabled': {
                  background: COLOR_DISABLED_BG,
                  color: COLOR_DISABLED_TEXT,
                },
                [theme.breakpoints.up('xs')]: {
                  py: '0.5rem',
                  fontSize: '0.8125rem',
                },
                [theme.breakpoints.up('sm')]: {
                  py: '0.625rem',
                  fontSize: '1rem',
                },
              }}
            >
              {intl.formatMessage(messages.registerLabelButtonCreateAccount)}
            </Button>
            {registerError && (
              <Typography variant="subtitle1" align="center" color={COLOR_WHITE} py="0.9375rem">
                {intl.formatMessage(messages.registerValidationErrorMessage)}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </ScrollableContainer>
  );
};
